import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, Button } from "reactstrap";
import { useReactToPrint } from "react-to-print";
import VirtualKeyboard from "./components/VirtualKeyboard";
import NumericKeyboard from "./components/NumericKeyboard";
import MemberList from "./components/MemberList";
import ThermalPrintableWorkout from "./components/ThermalPrintableWorkout";
import neoLogo from "../../assets/images/Neo_Logo_A.svg";
import new1Bg from "../../assets/images/new1Bg.png";
import { useKiosk } from "./hooks/useKiosk";
import { useTenant } from "../../hooks/useTenant";
import WorkoutPrescriptionService from "../../services/workout_prescription";


const WorkoutPrintScreen = () => {
    document.title = "Imprimir Treino | NeoApp";
    const navigate = useNavigate();
    const { tenantName } = useTenant();

    // Preload da imagem de fundo para evitar piscar
    const [bgLoaded, setBgLoaded] = useState(false);
    useEffect(() => {
        const img = new Image();
        img.src = new1Bg;
        img.onload = () => setBgLoaded(true);
        img.onerror = () => setBgLoaded(true);
    }, []);

    const {
        tenantLogo,
        tenantLoading,
        idTenant,
        loadingMembers,
        searchTerm,
        selectedMember,
        filteredMembers,
        debouncedSearchTerm,
        actions: {
            handleKeyPress,
            handleDelete,
            handleSpace,
            handleClear,
            handleSelectMember,
            handleBack,
            hideKeyboard,
            setSearchTerm
        }
    } = useKiosk();

    // Teclado: numérico (padrão) ou texto
    const [keyboardMode, setKeyboardMode] = useState("numeric");
    const [showResults, setShowResults] = useState(false);

    // No modo texto, mostra resultados automaticamente
    useEffect(() => {
        if (keyboardMode === "text" && debouncedSearchTerm && debouncedSearchTerm.trim().length >= 2) {
            setShowResults(true);
        } else if (keyboardMode === "text") {
            setShowResults(false);
        }
    }, [debouncedSearchTerm, keyboardMode]);

    // Treinos do membro selecionado
    const [prescriptions, setPrescriptions] = useState([]);
    const [loadingWorkouts, setLoadingWorkouts] = useState(false);
    const [selectedWorkout, setSelectedWorkout] = useState(null);
    const printRef = useRef(null);

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Treino_${selectedMember?.firstName || "Aluno"}`,
        pageStyle: `
            @page {
                size: 80mm auto;
                margin: 0mm;
            }
            @media print {
                body {
                    -webkit-print-color-adjust: exact;
                }
            }
        `,
        onAfterPrint: () => setSelectedWorkout(null),
    });

    // Busca treinos quando seleciona um membro
    useEffect(() => {
        if (!selectedMember?.id || !idTenant) {
            setPrescriptions([]);
            return;
        }
        const fetch = async () => {
            setLoadingWorkouts(true);
            try {
                const data = await WorkoutPrescriptionService.getActiveByMember(idTenant, selectedMember.id);
                const enriched = await Promise.all(
                    data.map(async (p) => {
                        try {
                            const sessions = await WorkoutPrescriptionService.getSessions(idTenant, selectedMember.id, p.id);
                            return { ...p, _sessions: sessions, _totalSessions: sessions.length, _completedCount: sessions.filter(s => s.status === "completed").length };
                        } catch { return { ...p, _sessions: [], _totalSessions: 0, _completedCount: 0 }; }
                    })
                );
                setPrescriptions(enriched);
            } catch (err) {
                console.error("Erro ao buscar treinos:", err);
            } finally {
                setLoadingWorkouts(false);
            }
        };
        fetch();
    }, [selectedMember?.id, idTenant]);

    // Identifica o próximo treino (treino de hoje)
    const nextWorkout = React.useMemo(() => {
        for (const p of prescriptions) {
            const allSessions = p._sessions || [];
            const nextPending = allSessions
                .filter(s => s.status === "pending")
                .sort((a, b) => (a.sessionNumber || 0) - (b.sessionNumber || 0))[0];
            if (nextPending && p.sessions?.[nextPending.workoutIndex]) {
                return {
                    workout: p.sessions[nextPending.workoutIndex],
                    prescription: p,
                    workoutIndex: nextPending.workoutIndex,
                    session: nextPending,
                };
            }
        }
        // Se não tem pendente, pega o primeiro treino disponível
        if (prescriptions.length > 0 && prescriptions[0].sessions?.length > 0) {
            return {
                workout: prescriptions[0].sessions[0],
                prescription: prescriptions[0],
                workoutIndex: 0,
                session: null,
            };
        }
        return null;
    }, [prescriptions]);

    const handleQuickPrint = async () => {
        if (!nextWorkout) return;
        const { workout, prescription, workoutIndex } = nextWorkout;
        const resolvedType = workout.workoutType || workout.blocks?.[0]?.workoutType || prescription.workoutType || "strength";
        setSelectedWorkout({
            ...workout,
            title: workout.name || workout.title || `Treino ${String.fromCharCode(65 + workoutIndex)}`,
            workoutType: resolvedType,
            teacherName: prescription.teacherName,
        });

        // Consome sessão se não foi feita hoje
        if (nextWorkout.session) {
            const today = new Date().toISOString().split("T")[0];
            const alreadyDoneToday = (prescription._sessions || []).some(s => {
                if (s.workoutIndex !== workoutIndex || s.status !== "completed") return false;
                const d = s.completedAt?.toDate ? s.completedAt.toDate() : s.completedAt instanceof Date ? s.completedAt : null;
                return d && d.toISOString().split("T")[0] === today;
            });
            if (!alreadyDoneToday) {
                try {
                    const next = await WorkoutPrescriptionService.getNextPendingSession(idTenant, selectedMember.id, prescription.id, workoutIndex);
                    if (next) {
                        await WorkoutPrescriptionService.completeSession(idTenant, selectedMember.id, prescription.id, next.id, "kiosk_print");
                    }
                } catch (e) { console.error(e); }
            }
        }

        setTimeout(() => handlePrint && handlePrint(), 200);
    };

    const handleGoToDetails = () => {
        // Navega para a página completa de detalhes do aluno
        navigate("/kiosk/print/details", { state: { member: selectedMember } });
    };

    const handleBackAndClear = () => {
        handleBack();
        handleClear();
        setKeyboardMode("numeric");
    };

    // ═══ Lógica de Inatividade ═══
    useEffect(() => {
        let idleTimer;
        const resetTimer = () => {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
                handleClear();
                handleBack();
                setKeyboardMode("numeric");
            }, 60000);
        };
        resetTimer();
        const handleActivity = () => resetTimer();
        const events = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'];
        events.forEach(e => window.addEventListener(e, handleActivity));
        return () => {
            clearTimeout(idleTimer);
            events.forEach(e => window.removeEventListener(e, handleActivity));
        };
    }, [handleClear, handleBack]);

    if (tenantLoading || loadingMembers || !bgLoaded) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", backgroundColor: "#f8f8fb" }}>
                <div className="text-center">
                    <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                    <p className="text-muted mt-3 fw-medium font-size-16">Carregando sistema...</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="d-flex flex-column"
            style={{
                minHeight: "100vh",
                position: "relative",
                backgroundColor: "#001D3D",
                backgroundImage: `url(${new1Bg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            <style>
                {`
                * {
                    backdrop-filter: none !important;
                    -webkit-backdrop-filter: none !important;
                }
                `}
            </style>

            {/* Overlay */}
            <div
                style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(240, 242, 246, 0.92)",
                    zIndex: 0,
                    willChange: "transform",
                    transform: "translateZ(0)"
                }}
            />

            {/* Conteúdo Principal */}
            <div className="d-flex flex-column" style={{ zIndex: 1, position: "relative", minHeight: "100vh" }}>
                {/* Header */}
                <div className="px-4 py-3 flex-shrink-0 d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        {selectedMember && (
                            <button
                                className="btn btn-sm text-muted me-3 d-flex align-items-center"
                                onClick={handleBackAndClear}
                            >
                                <i className="mdi mdi-arrow-left font-size-22 me-1" />
                                Voltar
                            </button>
                        )}
                        {tenantLogo && (
                            <img
                                src={tenantLogo}
                                alt="Logo Academia"
                                style={{ maxHeight: "45px", maxWidth: "160px", objectFit: "contain" }}
                            />
                        )}
                    </div>
                </div>

                {/* Área principal: lado a lado quando selecionado */}
                <div className="flex-grow-1 d-flex align-items-stretch justify-content-center px-3 py-3" style={{ overflow: "hidden" }}>

                    {/* ═══ Coluna Esquerda: Busca + Teclado ═══ */}
                    <div className="d-flex flex-column align-items-center h-100" style={{ width: selectedMember ? "50%" : "100%", maxWidth: selectedMember ? "500px" : "480px", transition: "all 0.3s ease" }}>

                        {/* Título */}
                        <h5 className="fw-bold text-dark mb-3 align-self-start flex-shrink-0" style={{ fontSize: "20px" }}>
                            {keyboardMode === "numeric" ? "Informe matrícula ou CPF" : "Buscar por nome"}
                        </h5>

                        {/* Input */}
                        <div className="w-100 mb-3 flex-shrink-0">
                            <input
                                type="text"
                                placeholder={keyboardMode === "numeric" ? "CPF ou ID" : "Digite o nome..."}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="fw-bold w-100 shadow-none"
                                style={{
                                    fontSize: "22px",
                                    color: "#482712",
                                    backgroundColor: "#ffffff",
                                    border: "2px solid #482712",
                                    borderRadius: "12px",
                                    padding: "14px 18px",
                                    fontFamily: "'Outfit', sans-serif",
                                    outline: "none",
                                    boxShadow: "none"
                                }}
                                readOnly
                            />
                        </div>

                        {/* Resultados (flex-grow para ocupar espaço entre input e teclado) */}
                        {showResults && filteredMembers.length > 0 && (
                            <div
                                className="w-100 mb-3 shadow-sm overflow-auto custom-scrollbar flex-grow-1"
                                style={{
                                    backgroundColor: "#fff",
                                    border: "1px solid #e0e0e0",
                                    minHeight: 0 // Importante para scroll funcionar no flex-grow
                                }}
                            >
                                <MemberList
                                    members={filteredMembers}
                                    loading={loadingMembers}
                                    onSelect={(m) => { handleSelectMember(m); hideKeyboard(); setShowResults(false); }}
                                />
                            </div>
                        )}

                        {/* Teclado */}
                        {!selectedMember && (
                            <div className="w-100 mt-auto flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                {keyboardMode === "numeric" ? (
                                    <NumericKeyboard
                                        onKeyPress={handleKeyPress}
                                        onDelete={(e) => { handleDelete(); setShowResults(false); }}
                                        onClear={() => { handleClear(); setShowResults(false); }}
                                        onConfirm={searchTerm.trim().length >= 2 ? () => setShowResults(true) : undefined}
                                        confirmLabel="CONTINUAR"
                                        confirmDisabled={searchTerm.trim().length < 2}
                                    />
                                ) : (
                                    <VirtualKeyboard
                                        onKeyPress={handleKeyPress}
                                        onDelete={handleDelete}
                                        onSpace={handleSpace}
                                        onClear={handleClear}
                                    />
                                )}

                                {/* Toggle numérico/texto */}
                                <div className="text-center mt-3">
                                    <button
                                        className="btn btn-sm text-primary fw-bold"
                                        onClick={() => { setKeyboardMode(keyboardMode === "numeric" ? "text" : "numeric"); handleClear(); }}
                                    >
                                        <i className={`mdi ${keyboardMode === "numeric" ? "mdi-alphabetical" : "mdi-numeric"} me-1`} />
                                        {keyboardMode === "numeric" ? "Buscar por nome" : "Buscar por matrícula"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ═══ Coluna Direita: Prévia do Treino ═══ */}
                    {selectedMember && (
                        <div className="d-flex flex-column h-100 ms-4" style={{ width: "50%", maxWidth: "420px", transition: "all 0.3s ease" }}>

                            {loadingWorkouts ? (
                                <div className="text-center py-5 bg-white rounded-4 shadow-lg d-flex flex-column align-items-center justify-content-center h-100">
                                    <Spinner color="primary" />
                                    <p className="text-muted mt-3 font-size-15 fw-bold">Buscando treinos...</p>
                                </div>
                            ) : nextWorkout ? (
                                <div className="w-100 h-100 bg-white shadow-lg d-flex flex-column p-4" style={{ borderRadius: "0", border: "1px solid rgba(0,0,0,0.05)" }}>

                                    {/* Header do Membro */}
                                    <div className="d-flex align-items-center mb-3 pb-3 border-bottom">
                                        {selectedMember.photoUrl ? (
                                            <img
                                                src={selectedMember.photoUrl}
                                                alt=""
                                                className="shadow-sm"
                                                style={{ width: 64, height: 64, objectFit: "cover", borderRadius: "0", border: "2px solid #fff" }}
                                            />
                                        ) : (
                                            <div className="shadow-sm bg-light d-flex align-items-center justify-content-center text-primary" style={{ width: 64, height: 64, borderRadius: "0" }}>
                                                <i className="mdi mdi-account font-size-24"></i>
                                            </div>
                                        )}
                                        <div className="ms-3 text-start overflow-hidden">
                                            <h5 className="fw-bold text-dark mb-1 text-truncate" style={{ fontSize: "18px" }}>{selectedMember.fullName}</h5>
                                            <span className="badge bg-primary-subtle text-primary font-size-12 px-2 py-1" style={{ borderRadius: "0" }}>
                                                Matrícula: {selectedMember.memberNumber || "N/A"}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="text-center mb-3">
                                        <h6 className="fw-bold text-dark mb-0 d-flex align-items-center justify-content-center">
                                            <i className="mdi mdi-calendar-check text-success me-2 font-size-20"></i>
                                            TREINO DE HOJE
                                        </h6>
                                    </div>

                                    {/* Prévia do treino - ocupa o espaço restante */}
                                    <div
                                        className="flex-grow-1 overflow-auto custom-scrollbar px-3 py-2 mb-4"
                                        style={{
                                            backgroundColor: "#f8f9fa",
                                            borderRadius: "0",
                                            border: "1px dashed #ced4da",
                                            minHeight: "0" // Essencial para scroll em flex child
                                        }}
                                    >
                                        <div className="d-flex justify-content-center">
                                            <ThermalPrintableWorkout
                                                workout={{
                                                    ...nextWorkout.workout,
                                                    title: nextWorkout.workout.name || nextWorkout.workout.title || `Treino ${String.fromCharCode(65 + nextWorkout.workoutIndex)}`,
                                                    workoutType: nextWorkout.workout.workoutType || nextWorkout.prescription.workoutType || "strength",
                                                    teacherName: nextWorkout.prescription.teacherName,
                                                }}
                                                tenantName={tenantName}
                                                memberName={selectedMember.fullName}
                                                tenantLogo={tenantLogo}
                                            />
                                        </div>
                                    </div>

                                    {/* Botões de Ação (Apenas Ícones) */}
                                    <div className="mt-auto flex-shrink-0 d-flex gap-3">
                                        <Button
                                            color="primary"
                                            className="fw-bold py-3 d-flex align-items-center justify-content-center shadow-sm flex-grow-1"
                                            onClick={handleQuickPrint}
                                            style={{ borderRadius: "0" }}
                                            title="Imprimir Ficha"
                                        >
                                            <i className="mdi mdi-printer font-size-24" />
                                        </Button>

                                        <button
                                            className="btn fw-bold py-3 d-flex align-items-center justify-content-center transition-all flex-grow-1"
                                            onClick={handleGoToDetails}
                                            style={{
                                                backgroundColor: "#f0f2f6",
                                                color: "#495057",
                                                borderRadius: "0",
                                                border: "none"
                                            }}
                                            title="Ver Todos os Treinos"
                                        >
                                            <i className="mdi mdi-format-list-bulleted font-size-24" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5 bg-white rounded-4 shadow-lg d-flex flex-column align-items-center justify-content-center h-100">
                                    <div className="rounded-circle bg-light d-flex align-items-center justify-content-center mb-3" style={{ width: 80, height: 80 }}>
                                        <i className="mdi mdi-clipboard-text-off-outline text-muted font-size-36" />
                                    </div>
                                    <h5 className="fw-bold text-dark">Nenhum Treino</h5>
                                    <p className="text-muted font-size-14 px-4">Este aluno não possui nenhum treino ativo no momento.</p>
                                    <button
                                        className="btn btn-outline-primary mt-2 rounded-pill px-4 fw-bold"
                                        onClick={handleGoToDetails}
                                    >
                                        Ver Perfil Completo
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 position-relative" style={{ zIndex: 10 }}>
                    <div className="d-flex justify-content-end align-items-center py-3 px-4">
                        <span style={{ fontSize: "11px", fontWeight: "800", color: "#b3b3b3", letterSpacing: "1px" }} className="me-2 text-uppercase">POWERED BY</span>
                        <img src={neoLogo} alt="NeoApp" style={{ height: "14px", filter: "grayscale(100%) opacity(0.5)" }} />
                    </div>
                </div>
            </div>

            {/* Hidden component for printing */}
            <div style={{ position: "absolute", overflow: "hidden", height: 0, width: 0 }}>
                <div ref={printRef}>
                    <ThermalPrintableWorkout
                        workout={selectedWorkout}
                        tenantName={tenantName}
                        memberName={selectedMember?.fullName}
                        tenantLogo={tenantLogo}
                    />
                </div>
            </div>
        </div>
    );
};

export default WorkoutPrintScreen;
